import { Component, ChangeDetectionStrategy, computed, effect, inject, input, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { TranslateModule } from "@ngx-translate/core";
import { ConnectedPosition } from "@angular/cdk/overlay";

import { UserContext } from "src/app/store/user/user.state";
import {
    selectCurrentUrl,
    selectIsCentralSupportOpen,
    selectIsContractAdminOpen,
    selectIsSuperUserOpen,
    selectIsDeveloperOpen,
} from "src/app/store/navigation/navigation.selectors";
import { NavigationActions } from "src/app/store/navigation/navigation.actions";
import { SidebarGroup } from "src/app/store/navigation/navigation.state";
import { SidenavSectionComponent } from "../../parts/sidenav-section/sidenav-section.component";
import { SidenavGroupComponent } from "../../parts/sidenav-group/sidenav-group.component";
import { SidenavGroup, SidenavItem, SidenavSection, SUBSET_MATCH_OPTIONS } from "../../parts/sidenav-nav.model";
import { SUPER_USER_SECTIONS } from "../super-user/super-user.config";
import { DEVELOPER_SECTIONS } from "../developer/developer.config";
import { CONTRACT_ADMIN_SECTIONS } from "../contract-admin/contract-admin.config";
import { CENTRAL_SUPPORT_SECTIONS } from "../central-support/central-support.config";

type FlyoutGroup = SidebarGroup;

@Component({
    selector: "app-home-view",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, SidenavSectionComponent, SidenavGroupComponent],
    templateUrl: "./home-view.component.html",
    styleUrl: "./home-view.component.scss",
})
export class HomeViewComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly store = inject(Store);

    // =========================================================================
    // INPUTS
    // =========================================================================
    public readonly expanded = input.required<boolean>();
    public readonly user = input.required<UserContext | null>();

    // =========================================================================
    // NAV CONFIG - edit these arrays to add/remove/reorder items
    // =========================================================================
    public readonly mainItems = computed<SidenavSection>(() => {
        const items: SidenavItem[] = [
            { labelKey: "HEADER.ALL_QUOTES", icon: "envelope", segments: ["quote"], routerLinkActiveOptions: SUBSET_MATCH_OPTIONS },
            { labelKey: "HEADER.ALL_CUSTOMERS", icon: "users", segments: ["customer"], routerLinkActiveOptions: SUBSET_MATCH_OPTIONS },
            { labelKey: "HEADER.ALL_CONTACTS", icon: "id-card", segments: ["contact"], routerLinkActiveOptions: SUBSET_MATCH_OPTIONS },
            { labelKey: "HEADER.ALL_CONTRACTS", icon: "file-check", segments: ["contract"], routerLinkActiveOptions: SUBSET_MATCH_OPTIONS },
        ];

        // Catalogs only visible for super users
        if (this.isSuperUser()) {
            items.push({ labelKey: "CATALOG_PREVIEW.ALL_CATALOGS", icon: "book", segments: ["catalogs"], routerLinkActiveOptions: SUBSET_MATCH_OPTIONS });
        }

        items.push({ labelKey: "HEADER.ALL_VENDORS", icon: "truck", segments: ["vendor"], routerLinkActiveOptions: SUBSET_MATCH_OPTIONS });

        return { items };
    });

    public readonly helpItems: SidenavSection = {
        items: [
            { labelKey: "HELP.RELEASE_NOTES", icon: "info-circle", segments: ["home", "release-notes"] },
        ],
    };

    public readonly centralSupportGroup: SidenavGroup = {
        key: "central-support", labelKey: "CENTRAL_SUPPORT.TITLE", icon: "headphones", sections: CENTRAL_SUPPORT_SECTIONS,
    };

    public readonly contractAdminGroup: SidenavGroup = {
        key: "contract-admin", labelKey: "ADMIN.CONTRACT_ADMIN.TITLE", icon: "file-edit", sections: CONTRACT_ADMIN_SECTIONS,
    };

    public readonly datasetAdminItem: SidenavItem = {
        labelKey: "ADMIN.SUPER_USER.DATASETS.TITLE", icon: "database", segments: ["contract-admin", "datasets"],
    };

    public readonly superUserGroup: SidenavGroup = {
        key: "super-user", labelKey: "SUPERUSER", icon: "shield", sections: SUPER_USER_SECTIONS,
    };

    public readonly developerGroup: SidenavGroup = {
        key: "developer", labelKey: "DEVELOPER.MENU_TITLE", icon: "code", sections: DEVELOPER_SECTIONS,
    };

    // =========================================================================
    // COLLAPSED FLY-OUT - which group's fly-out is open (null = none)
    // =========================================================================
    public readonly flyoutGroup = signal<FlyoutGroup | null>(null);

    // Anchor the fly-out to the RIGHT of the rail icon, top-aligned; flip to
    // bottom-aligned when there isn't room below the trigger.
    public readonly flyoutPositions: ConnectedPosition[] = [
        { originX: "end", originY: "top", overlayX: "start", overlayY: "top" },
        { originX: "end", originY: "bottom", overlayX: "start", overlayY: "bottom" },
    ];

    // Short delay so moving the cursor from the icon into the (flush) panel
    // doesn't close the fly-out between the two mouseleave/mouseenter events.
    private closeTimer: ReturnType<typeof setTimeout> | null = null;

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    private readonly currentUrl = toSignal(this.store.select(selectCurrentUrl), { initialValue: "" });
    public readonly centralSupportOpen = toSignal(this.store.select(selectIsCentralSupportOpen), { initialValue: false });
    public readonly contractAdminOpen = toSignal(this.store.select(selectIsContractAdminOpen), { initialValue: false });
    public readonly superUserOpen = toSignal(this.store.select(selectIsSuperUserOpen), { initialValue: false });
    public readonly developerOpen = toSignal(this.store.select(selectIsDeveloperOpen), { initialValue: false });

    // =========================================================================
    // COMPUTED - Permissions
    // =========================================================================
    public readonly canAccessCentralSupport = computed(() => {
        const u = this.user();
        return u?.userRoles?.developer || u?.userRoles?.superUser || u?.userRoles?.centralSalesSupport;
    });

    public readonly canAccessContractAdmin = computed(() => {
        const u = this.user();
        return u?.userRoles?.developer || u?.userRoles?.superUser || u?.userRoles?.contractAdmin;
    });

    public readonly isSuperUser = computed(() => this.user()?.userRoles?.superUser);
    public readonly isDeveloper = computed(() => this.user()?.userRoles?.developer);

    // =========================================================================
    // COMPUTED - Section visibility / active group
    // =========================================================================
    public readonly showAdminSection = computed(() => this.canAccessCentralSupport() || this.canAccessContractAdmin() || this.isSuperUser() || this.isDeveloper());
    // Exchange rates lives under the Superuser menu (though its route is /home/exchange-rates),
    // and sales orders under the Developer menu (route /sales-order), so treat them as part of
    // their section for highlighting / auto-open.
    public readonly isCentralSupportActive = computed(() => this.currentUrl().includes("/central-support"));
    public readonly isSuperUserActive = computed(() => this.currentUrl().includes("/super-user") || this.currentUrl().includes("/exchange-rates"));
    public readonly isDeveloperActive = computed(() => this.currentUrl().includes("/developer") || this.currentUrl().includes("/sales-order"));
    public readonly isContractAdminActive = computed(() => this.currentUrl().includes("/contract-admin"));

    // =========================================================================
    // EFFECTS
    // =========================================================================
    // When collapsed, clicking an item inside a fly-out navigates but leaves the
    // fly-out open — close it on every route change.
    private readonly closeFlyoutsOnNav = effect(() => {
        this.currentUrl();
        this.flyoutGroup.set(null);
    });

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public toggleCentralSupport(): void {
        this.store.dispatch(NavigationActions.toggleSidebarGroup({ group: "central-support" }));
    }

    public toggleContractAdmin(): void {
        this.store.dispatch(NavigationActions.toggleSidebarGroup({ group: "contract-admin" }));
    }

    public toggleSuperUser(): void {
        this.store.dispatch(NavigationActions.toggleSidebarGroup({ group: "super-user" }));
    }

    public toggleDeveloper(): void {
        this.store.dispatch(NavigationActions.toggleSidebarGroup({ group: "developer" }));
    }

    // Collapsed rail: hover opens a group's fly-out (only one open at a time).
    public openFlyout(group: FlyoutGroup): void {
        this.cancelCloseFlyout();
        this.flyoutGroup.set(group);
    }

    public scheduleCloseFlyout(): void {
        this.cancelCloseFlyout();
        this.closeTimer = setTimeout(() => this.flyoutGroup.set(null), 120);
    }

    public cancelCloseFlyout(): void {
        if (this.closeTimer) {
            clearTimeout(this.closeTimer);
            this.closeTimer = null;
        }
    }

    public closeFlyout(): void {
        this.cancelCloseFlyout();
        this.flyoutGroup.set(null);
    }
}
